import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";

/**
 * Stream lifecycle + capture + torch + lens stops for the in-app burst camera.
 *
 * Owns a single `getUserMedia` MediaStream (`facingMode: "environment"`, no
 * audio), the live `<video>` element it feeds, torch (flashlight) capability
 * detection + toggle, the zoom model (one continuous native axis
 * `[zoomFloor, zoomCeiling]` — fused cameras reach 0.5× natively; a digital
 * center-crop or a separate ultra-wide `deviceId` are the fallbacks), and the
 * frame → JPEG capture. Acquires when `isOpen` flips true; tears every track
 * down on close / unmount / `pagehide` so the camera light never stays on and
 * the stream never leaks. See [[burst-camera.spec]] "Stream Lifecycle &
 * Cleanup" and "Lens Stops & Zoom".
 */

export type CameraStatus =
  | "idle"
  | "loading"
  | "ready"
  | "denied"
  | "no-camera"
  | "error";

export interface UseCameraStreamResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  status: CameraStatus;
  /**
   * `undefined` while we haven't probed yet; `false` when the track can't do
   * torch (iOS Safari, most desktop webcams) → the flash button must be
   * hidden, never shown dead; `true` when supported.
   */
  torchSupported: boolean | undefined;
  torchOn: boolean;
  toggleTorch: () => Promise<void>;
  /** Draws the current <video> frame to a canvas (center-cropped by the live
   *  digital zoom factor on no-native-zoom devices, full frame when
   *  natively/optically zoomed, never upscaled) and resolves a JPEG blob. */
  captureFrame: () => Promise<Blob>;
  /** Re-run getUserMedia after a denied / error state. */
  retry: () => void;
  /**
   * Preset tap-stop factors, ascending (e.g. [0.5,1,2,3]). Always contains 1.
   * `0.5` requires EITHER a native range reaching ≤0.5 (fused camera) OR a
   * matched ultra-wide device; `2`/`3` require the range to reach that factor.
   * ≤1 entry → the component renders no stop row (today's UI, unchanged).
   */
  lensStops: number[];
  /** Current factor. On a native range this is the live zoom (can be <1, e.g. 0.5
   *  on a fused camera); on the device-switch ultra-wide fallback it is pinned 0.5. */
  activeZoom: number;
  /** Widest zoom-OUT factor the current track allows: `zoom.min` when it reports a
   *  native range with min<1, else `1`. Below 1 only on a native (fused) lens. */
  zoomFloor: number;
  /** Max zoom-IN factor on the current track. `min(5, zoom.max)` native, `2`/`1`
   *  digital, `1` on the device-switch ultra-wide. Pinch binds when
   *  `zoomCeiling > zoomFloor`. */
  zoomCeiling: number;
  /** CSS scale the component applies to the <video> preview: equals the digital
   *  zoom factor (no-native-zoom devices), else 1 (native zoom moves the real lens). */
  previewScale: number;
  /** Continuous pinch setter — clamps to [zoomFloor, zoomCeiling]; no-op on the
   *  device-switch ultra-wide fallback or while not ready. */
  setZoom: (factor: number) => void;
  /** Tap a preset stop. `0.5` ↔ a wide factor crosses physical lenses
   *  (re-acquire); a wide preset snaps the zoom. No-op for an unavailable stop
   *  or while not ready. */
  selectStop: (factor: number) => void;
}

const JPEG_QUALITY = 0.92;

/**
 * How long a user-initiated `retry()` may run before we give up on the current
 * document and reload. Long enough that it never fires while a healthy
 * permission prompt is still on screen (a user reading the dialog on a live
 * process resolves well within this); short enough to recover a truly wedged
 * getUserMedia that never settles. The common stuck cases (iOS standalone after
 * 3 "Don't Allow" taps, or a backgrounded-then-killed web process) reject
 * instantly and reload via the status watcher, not this timeout.
 */
export const RETRY_RELOAD_TIMEOUT_MS = 12_000;

/**
 * Full 4:3 sensor at 12 MP, shared by the default-lens and ultra-wide acquire
 * paths. iOS/Android grant 4032×3024 for a 4:3 request — the sensor's *native*
 * readout; a 16:9 request crops ~40% of the vertical FOV (and ~1/3 of the
 * pixels) off it (iPhone / most Android main sensors are natively 4:3, and 16:9
 * is a top+bottom crop). `ideal` degrades gracefully on weaker cameras; iOS
 * defaults to 640×480 with no request. See [[burst-camera.spec]] Decision #1.
 */
const IDEAL_FULL_SENSOR = { width: { ideal: 4032 }, height: { ideal: 3024 } } as const;

/**
 * 2× center-crop floor: below a 2560px longest edge a 2× crop stores sub-720p
 * documentation — not acceptable evidence, so the stop is not offered.
 */
const MIN_2X_LONGEST_EDGE = 2560;

/** Digital-zoom ceiling for no-native-zoom devices: a 2× center-crop of the full
 *  4:3 sensor (4032×3024) still stores ~3 MP — above the evidence floor. Past 2× a
 *  digital crop is too soft. (Native-zoom devices ignore this — the lens zooms.) */
const DIGITAL_ZOOM_MAX = 2;
/** Native-zoom ceiling: the reported range runs into mushy high digital zoom at
 *  the top; cap at 5× to capture the optical telephoto on modern Pros while
 *  staying out of deep digital slop. */
const NATIVE_ZOOM_MAX_CAP = 5;
/** Preset tap-stop factors, intersected with what the current lens offers. */
const CANDIDATE_STOPS = [0.5, 1, 2, 3] as const;
/** A factor counts as "on" a stop within this tolerance (highlight + hide the
 *  off-stop readout pill). Exported for the component. */
export const ZOOM_EPSILON = 0.05;

/**
 * Rear ultra-wide classifier ([[burst-camera.spec]] "Lens Stops"): the label
 * must look ultra-wide and must NOT be a Dual/Triple VIRTUAL auto-switching
 * device or a front camera. Localized labels simply fail to match → the 0.5×
 * stop is not offered (degraded, never broken).
 */
function isUltraWideLabel(label: string): boolean {
  return /ultra.?wide/i.test(label) && !/dual|triple|front/i.test(label);
}

/**
 * Fresh ultra-wide lookup. Device ids rotate per session on iOS, so the id is
 * re-resolved on every acquire that needs it and never persisted. Resolves
 * null on an absent API, enumeration failure, or no labelled match — never
 * throws: the lens feature must never be able to break base capture.
 */
async function findUltraWideDeviceId(): Promise<string | null> {
  try {
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices || typeof mediaDevices.enumerateDevices !== "function") {
      return null;
    }
    const devices = await mediaDevices.enumerateDevices();
    const match = devices.find(
      (device) =>
        device.kind === "videoinput" && isUltraWideLabel(device.label),
    );
    return match?.deviceId ?? null;
  } catch {
    return null;
  }
}

/**
 * Non-standard torch capability/constraint. `getCapabilities()` and the
 * `torch` advanced constraint are not in the base lib.dom MediaTrack types,
 * so we narrow locally rather than casting at every call site.
 */
interface TorchCapabilities {
  torch?: boolean;
}

function trackSupportsTorch(track: MediaStreamTrack): boolean {
  // `getCapabilities` is itself missing on some engines (older Firefox,
  // Safari) — guard the method before calling it.
  if (typeof track.getCapabilities !== "function") return false;
  try {
    const caps = track.getCapabilities() as TorchCapabilities;
    return caps.torch === true;
  } catch {
    return false;
  }
}

/**
 * Non-standard zoom capability. `getCapabilities().zoom` is exposed by any browser
 * on a zoom-capable device — this INCLUDES modern multi-lens iPhones, whose fused
 * rear camera reports a range like `{min:0.5, max:10}` (verified on-device
 * 2026-07-09), and most Android. It is absent on single-lens / older phones and
 * desktop webcams. Its presence is the native-vs-digital engine switch — a device
 * capability, NOT a platform (do not assume "iOS = no zoom"; that was false).
 */
interface ZoomCapabilities {
  zoom?: { min: number; max: number; step?: number };
}

/**
 * The track's native zoom range, or null when the device exposes no `zoom`
 * capability (single-lens / older phones, desktop webcams — NOT iOS as a class)
 * or the range can't zoom in. Never throws — capability probes must not break
 * acquire.
 */
function readNativeZoom(
  track: MediaStreamTrack,
): { min: number; max: number } | null {
  if (typeof track.getCapabilities !== "function") return null;
  try {
    const z = (track.getCapabilities() as ZoomCapabilities).zoom;
    if (
      z &&
      typeof z.min === "number" &&
      typeof z.max === "number" &&
      z.max > 1
    ) {
      return { min: z.min, max: z.max };
    }
  } catch {
    /* fall through */
  }
  return null;
}

/** Granted track resolution, read fail-safe (`getSettings` is missing or
 *  throwing on some engines — degrade to "unknown", never break acquire). */
function readGrantedResolution(track: MediaStreamTrack | null): {
  width?: number;
  height?: number;
} {
  if (!track || typeof track.getSettings !== "function") return {};
  try {
    const { width, height } = track.getSettings();
    return { width, height };
  } catch {
    return {};
  }
}

/**
 * Field telemetry: what resolution did the device ACTUALLY grant (vs the 4K
 * ideal we request), on which lens (`zoom` = the active factor), and which zoom
 * engine the wide lens picked. See [[burst-camera.spec]] "Stream Lifecycle".
 * No PII — labels are hardware names. Fail-safe by construction: a
 * missing/erroring Sentry must never break camera acquisition.
 */
function recordAcquireBreadcrumb(data: {
  width?: number;
  height?: number;
  label?: string;
  zoom: number;
  engine: "native" | "digital";
}): void {
  try {
    Sentry.addBreadcrumb({
      category: "camera",
      level: "info",
      message: "camera stream acquired",
      data,
    });
  } catch {
    // Telemetry only — acquisition proceeds regardless.
  }
}

/**
 * Resolve once the video has a decodable frame (`videoWidth > 0`), or after
 * `timeoutMs`. The caller re-checks dimensions and decides whether to proceed
 * or reject — this only avoids busy-failing the shutter during the brief gap
 * between `getUserMedia` resolving and the first frame painting.
 */
function waitForVideoFrame(
  video: HTMLVideoElement,
  timeoutMs: number,
): Promise<void> {
  if (video.videoWidth > 0 && video.videoHeight > 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const start = Date.now();
    const tick = () => {
      if (
        (video.videoWidth > 0 && video.videoHeight > 0) ||
        Date.now() - start >= timeoutMs
      ) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

function classifyGetUserMediaError(err: unknown): CameraStatus {
  if (err instanceof DOMException) {
    if (err.name === "NotAllowedError" || err.name === "SecurityError") {
      return "denied";
    }
    if (
      err.name === "NotFoundError" ||
      err.name === "DevicesNotFoundError" ||
      err.name === "OverconstrainedError"
    ) {
      return "no-camera";
    }
  }
  return "error";
}

export function useCameraStream(isOpen: boolean): UseCameraStreamResult {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const captureCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const [status, setStatus] = useState<CameraStatus>("idle");
  const [torchSupported, setTorchSupported] = useState<boolean | undefined>(
    undefined,
  );
  const [torchOn, setTorchOn] = useState(false);
  // Bumped by `retry()` to re-trigger acquisition without toggling `isOpen`.
  const [acquireNonce, setAcquireNonce] = useState(0);

  // Retry → reload escalation. On an installed iOS PWA an in-document re-acquire
  // cannot recover once WebKit has stopped prompting (≥3 "Don't Allow" taps) or
  // the web process was killed while backgrounded (the camera subsystem returns
  // wedged): a plain re-getUserMedia rejects instantly with no prompt, so "Try
  // again" looks dead. A fresh document is the only reliable reset, so a failed
  // user-initiated retry escalates to `location.reload()`. Armed ONLY by the
  // explicit tap (never the gesture-less visibility re-acquire) → no reload-loop.
  const retryPendingRef = useRef(false);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRetryWatchdog = useCallback(() => {
    retryPendingRef.current = false;
    if (retryTimeoutRef.current !== null) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  // Numeric lens model: one continuous zoom axis `[zoomFloor, zoomCeiling]` on the
  // current track (fused cameras reach 0.5 natively → `zoomFloor` < 1). `0.5` may
  // also be a separate ultra-wide DEVICE (the fallback). `activeZoom` derived below.
  const [onUltraWide, setOnUltraWide] = useState(false);
  const [wideZoom, setWideZoom] = useState(1); // zoom on the wide/fused lens
  const [lensStops, setLensStops] = useState<number[]>([1]);
  const [zoomCeiling, setZoomCeiling] = useState(1);
  // Widest zoom-OUT factor the current track allows. Below 1 only on a native
  // (fused) lens whose reported `zoom.min` is < 1 (modern iPhones report 0.5);
  // digital crop can only zoom in, so it stays 1.
  const [zoomFloor, setZoomFloor] = useState(1);
  const [previewScale, setPreviewScale] = useState(1);
  // Acquire target lens (survives re-acquire: retry / visibility-return).
  // Replaces the old `desiredStopRef === "0.5x"`.
  const wantUltraWideRef = useRef(false);
  // Zoom to apply once a wide re-acquire (e.g. returning from ultra-wide)
  // completes — clamped to the recomputed ceiling.
  const pendingWideZoomRef = useRef(1);
  // Current wide track's native zoom range (null = digital engine).
  const nativeZoomRef = useRef<{ min: number; max: number } | null>(null);
  // Last wide-lens stop set (e.g. [1], [1,2], [1,2,3]), discovered on the wide
  // acquire and remembered so the stop row stays a STABLE union while on the
  // ultra-wide lens — the 2×/3× chips must not vanish when the user taps 0.5×
  // (they belong to the wide lens; tapping one re-acquires it). The first
  // acquire is always the wide lens, so this is populated before any ultra-wide
  // switch can happen.
  const wideStopsRef = useRef<number[]>([1]);
  // Serialize native applyConstraints: apply the latest, drop intermediates.
  const zoomBusyRef = useRef(false);
  const zoomPendingRef = useRef<number | null>(null);

  const teardown = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      // Stopping the track implicitly turns off the torch — no explicit
      // "torch off" call, which avoids an applyConstraints race on teardown.
      stream.getTracks().forEach((t) => t.stop());
    }
    streamRef.current = null;
    trackRef.current = null;
    const video = videoRef.current;
    if (video) video.srcObject = null;
    setTorchOn(false);
  }, []);

  // Apply a native (optical) zoom factor, serialized: while one applyConstraints
  // is in flight, only the LATEST requested factor is kept and applied next —
  // high-frequency pinch updates never flood the device. A device refusal is
  // swallowed (keep the last good zoom). Declared before the acquire effect so
  // a returning re-acquire can restore a pending zoom.
  const applyNativeZoom = useCallback(
    (track: MediaStreamTrack, factor: number) => {
      // Inner recursion lives in a plain function (not the useCallback binding)
      // so the latest-wins drain can call itself without referencing the memo.
      const pump = async (f: number): Promise<void> => {
        if (zoomBusyRef.current) {
          zoomPendingRef.current = f; // coalesce: keep only the latest
          return;
        }
        zoomBusyRef.current = true;
        try {
          await track.applyConstraints({
            advanced: [{ zoom: f } as unknown as MediaTrackConstraintSet],
          });
        } catch {
          /* device refused — keep the last good zoom */
        }
        zoomBusyRef.current = false;
        if (zoomPendingRef.current != null) {
          const next = zoomPendingRef.current;
          zoomPendingRef.current = null;
          void pump(next);
        }
      };
      void pump(factor);
    },
    [],
  );

  // Acquire / release the stream as the overlay opens and closes. State
  // resets on close live in the cleanup (not the synchronous body) so we
  // synchronize React → the MediaStream external system without a cascading
  // synchronous setState in the effect body.
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    async function acquire() {
      // `mediaDevices` is absent on insecure origins and very old engines.
      if (
        typeof navigator === "undefined" ||
        !navigator.mediaDevices ||
        typeof navigator.mediaDevices.getUserMedia !== "function"
      ) {
        if (!cancelled) {
          setStatus("no-camera");
          setTorchSupported(false);
        }
        return;
      }

      setStatus("loading");
      setTorchSupported(undefined);

      // The lens this acquire is aiming for. A failed ultra-wide switch
      // downgrades to the wide lens rather than ever wedging the camera.
      const wantUltraWide = wantUltraWideRef.current;
      let onUltraWideLens = wantUltraWide;
      let stream: MediaStream | null = null;

      // 0.5× targets the physical ultra-wide by deviceId, re-resolved via a
      // FRESH enumeration on every acquire (ids rotate per session on iOS —
      // never persist one). The current tracks were already stopped by this
      // effect's cleanup, so the rear camera is free to re-open (iOS allows
      // only one live rear stream).
      if (wantUltraWide) {
        const ultraWideId = await findUltraWideDeviceId();
        if (cancelled) return;
        if (ultraWideId) {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              // No facingMode alongside an exact deviceId — the id IS the lens.
              video: { deviceId: { exact: ultraWideId }, ...IDEAL_FULL_SENSOR },
              audio: false,
            });
          } catch {
            // Failed ultra-wide switch → fall back to a fresh default-lens
            // acquire below; the selected lens must never wedge the camera.
            stream = null;
          }
          if (cancelled) {
            stream?.getTracks().forEach((t) => t.stop());
            return;
          }
        }
        if (!stream) onUltraWideLens = false; // unmatched or rejected → downgrade
      }

      if (!stream) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: "environment",
              // Request the FULL 4:3 sensor via `ideal` — phones grant 4032×3024
              // (12 MP, the native readout); a 16:9 request would crop it. `ideal`
              // degrades on weaker hardware (never OverconstrainedError). Without a
              // request iOS defaults to 640×480. The acquire breadcrumb records the
              // grant.
              ...IDEAL_FULL_SENSOR,
            },
            audio: false,
          });
        } catch (err) {
          if (cancelled) return;
          setStatus(classifyGetUserMediaError(err));
          setTorchSupported(false);
          return;
        }
        if (cancelled) {
          // The overlay closed (or a newer acquire ran) while we awaited —
          // never keep an orphaned stream alive.
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
      }

      streamRef.current = stream;
      const [track] = stream.getVideoTracks();
      trackRef.current = track ?? null;

      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        // iOS needs playsinline + muted (set on the element) to autoplay
        // inside the already-gestured overlay open. play() may reject if
        // interrupted — non-fatal.
        void video.play().catch(() => {});
      }

      // The granted resolution drives the digital-crop floor: a 2× crop needs
      // ≥2560px on the LONGEST edge (iOS reports orientation-flipped dimensions
      // in portrait), or it would store sub-720p documentation.
      const resolution = readGrantedResolution(track ?? null);
      const longestEdge = Math.max(
        resolution.width ?? 0,
        resolution.height ?? 0,
      );

      if (onUltraWideLens) {
        // Device-switch ultra-wide: a SEPARATE physical lens, no pinch, factor
        // pinned at 0.5. The stop row stays a STABLE union — 0.5 plus the
        // remembered wide stops — so the 2×/3× chips never vanish when the user
        // taps 0.5×. They belong to the wide lens: tapping one re-acquires the
        // wide lens at that factor. Only the highlight moves to 0.5.
        nativeZoomRef.current = null;
        setOnUltraWide(true);
        setWideZoom(1);
        setZoomCeiling(1);
        setZoomFloor(1);
        setPreviewScale(1);
        setLensStops([0.5, ...wideStopsRef.current]);
      } else {
        // On the wide/fused lens: pick the engine (native vs digital), the
        // zoom-OUT floor, and the zoom-IN ceiling.
        const native = track ? readNativeZoom(track) : null;
        nativeZoomRef.current = native;
        const cropFloorMet = longestEdge >= MIN_2X_LONGEST_EDGE;
        const ceiling = native
          ? Math.min(NATIVE_ZOOM_MAX_CAP, native.max)
          : cropFloorMet
            ? DIGITAL_ZOOM_MAX
            : 1;
        // A native range whose min is below 1 (fused multi-lens cameras — modern
        // iPhones report {min:0.5,…}) lets pinch/chip widen to the ultra-wide FOV
        // on the SAME track. Digital crop can only zoom IN → floor stays 1.
        const floor = native && native.min < 1 ? native.min : 1;
        setZoomFloor(floor);
        setZoomCeiling(ceiling);
        setOnUltraWide(false);

        // Restore any pending zoom from a returning re-acquire, clamped to range.
        const initialZoom = Math.min(
          ceiling,
          Math.max(floor, pendingWideZoomRef.current),
        );
        pendingWideZoomRef.current = 1;
        setWideZoom(initialZoom);
        setPreviewScale(native ? 1 : initialZoom);
        if (native && track && initialZoom !== 1) {
          void applyNativeZoom(track, initialZoom);
        }

        // Presets: 1 always; 2 iff ceiling≥2; 3 iff ceiling≥3; 0.5 iff the native
        // range reaches it (fused). 0.5 is ALSO added by the post-ready ultra-wide
        // DEVICE discovery below (the device-switch fallback). Remember only the
        // ≥1 wide stops so a later device-switch keeps the row stable.
        const stops = CANDIDATE_STOPS.filter(
          (f) =>
            f === 1 ||
            (f > 1 && f <= ceiling) ||
            (f < 1 && floor <= f + ZOOM_EPSILON),
        );
        wideStopsRef.current = stops.filter((f) => f >= 1);
        setLensStops(stops);
      }

      setTorchSupported(track ? trackSupportsTorch(track) : false);
      setStatus("ready");
      recordAcquireBreadcrumb({
        ...resolution,
        label: track?.label,
        zoom: onUltraWideLens ? 0.5 : 1,
        engine: nativeZoomRef.current ? "native" : "digital",
      });

      // Stop discovery — AFTER ready, so a slow/odd enumerateDevices can never
      // delay the shutter: does a rear ultra-wide exist for the 0.5× stop?
      // (Already on it → known.) Absent API / failure / no match leaves the
      // row as built above — degraded, never broken.
      if (!onUltraWideLens) {
        const ultraWideId = await findUltraWideDeviceId();
        if (cancelled || !ultraWideId) return;
        setLensStops((prev) => (prev.includes(0.5) ? prev : [0.5, ...prev]));
      }
    }

    void acquire();

    return () => {
      cancelled = true;
      teardown();
      // Reset status/torch on close (or before re-acquiring). Running in
      // cleanup keeps the effect body free of synchronous setState.
      setStatus("idle");
      setTorchSupported(undefined);
    };
  }, [isOpen, acquireNonce, teardown, applyNativeZoom]);

  // Reset the lens-stop state only on FULL close — deliberately NOT in the
  // acquire effect's cleanup, which also runs between re-acquires (retry,
  // visibility-return, lens switch) where the selected stop must survive so
  // the next acquire can restore it. Keyed on `isOpen` alone so this cleanup
  // fires only when the overlay actually closes (or unmounts).
  useEffect(() => {
    if (!isOpen) return;
    return () => {
      wantUltraWideRef.current = false;
      pendingWideZoomRef.current = 1;
      nativeZoomRef.current = null;
      wideStopsRef.current = [1];
      setOnUltraWide(false);
      setWideZoom(1);
      setLensStops([1]);
      setZoomCeiling(1);
      setZoomFloor(1);
      setPreviewScale(1);
    };
  }, [isOpen]);

  // Release the camera when the app is backgrounded so the rear camera + torch
  // never stay live in the user's pocket. On mobile, switching apps / locking
  // the screen fires `visibilitychange` (`document.hidden`), where `pagehide`
  // is unreliable — so visibility is the primary trigger and `pagehide` is the
  // unload / bfcache fallback.
  //
  // hidden  → tear down (stop tracks → kills torch) and drop back to a loading
  //           state so the UI never shows a live-looking-but-dead viewfinder.
  // visible → re-acquire by bumping the acquire nonce (the acquire effect's
  //           cleanup tears down the dead stream, then re-runs getUserMedia).
  useEffect(() => {
    if (!isOpen) return;

    const onVisibilityChange = () => {
      // A retry's reload arm is trusted only while the app stays foregrounded on
      // the SAME acquire. Any visibility transition supersedes an in-flight
      // retry — the re-acquire that follows a return is gesture-less — so drop
      // the arm here; otherwise that gesture-less re-acquire's denied/error
      // outcome could trigger the reload, violating "reload only from the tap".
      // The user can simply tap "Try again" again if they still need it.
      clearRetryWatchdog();
      if (document.hidden) {
        teardown();
        setStatus("loading");
        setTorchSupported(undefined);
      } else {
        setAcquireNonce((n) => n + 1);
      }
    };
    const onPageHide = () => teardown();

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [isOpen, teardown, clearRetryWatchdog]);

  const toggleTorch = useCallback(async () => {
    const track = trackRef.current;
    if (!track || !trackSupportsTorch(track)) return;
    const next = !torchOn;
    try {
      // `torch` is a non-standard advanced constraint absent from lib.dom's
      // MediaTrackConstraintSet — cast the advanced entry, not the whole call.
      await track.applyConstraints({
        advanced: [{ torch: next } as unknown as MediaTrackConstraintSet],
      });
      setTorchOn(next);
    } catch {
      // The device advertised torch but refused the constraint — leave the
      // toggle where it was; nothing to surface to the user.
    }
  }, [torchOn]);

  const captureFrame = useCallback(async (): Promise<Blob> => {
    const video = videoRef.current;
    if (!video) throw new Error("Camera not ready");

    // The stream can report `ready` a beat before the <video> has decoded its
    // first frame (videoWidth/Height still 0). Rather than failing the shutter
    // press, wait briefly for a real frame to land. Bounded so a genuinely
    // frameless track still rejects instead of hanging.
    await waitForVideoFrame(video, 2000);

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) throw new Error("Camera frame not available");

    // Capture at the video's INTRINSIC resolution (not the letterboxed CSS
    // box) so the stored JPEG is full quality.
    let canvas = captureCanvasRef.current;
    if (!canvas) {
      canvas = document.createElement("canvas");
      captureCanvasRef.current = canvas;
    }
    // Digital zoom = uniform center-crop at SOURCE resolution by the live
    // factor (crop, never upscale). Native optical zoom already moved the lens,
    // so it stores the full frame; the ultra-wide lens never crops. Floored so
    // odd intrinsic dimensions can't produce fractional drawImage coords.
    const cropFactor = !onUltraWide && !nativeZoomRef.current ? wideZoom : 1;
    if (cropFactor > 1) {
      const cropWidth = Math.floor(width / cropFactor);
      const cropHeight = Math.floor(height / cropFactor);
      canvas.width = cropWidth;
      canvas.height = cropHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas 2D context unavailable");
      ctx.drawImage(
        video,
        Math.floor((width - cropWidth) / 2),
        Math.floor((height - cropHeight) / 2),
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight,
      );
    } else {
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas 2D context unavailable");
      ctx.drawImage(video, 0, 0, width, height);
    }

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to capture frame"));
        },
        "image/jpeg",
        JPEG_QUALITY,
      );
    });
  }, [onUltraWide, wideZoom]);

  const retry = useCallback(() => {
    // Arm the reload escalation for THIS attempt, then re-acquire. The status
    // watcher (below) reloads if the acquire lands back on denied/error; the
    // timeout is the backstop for a getUserMedia that never settles at all.
    retryPendingRef.current = true;
    if (retryTimeoutRef.current !== null) clearTimeout(retryTimeoutRef.current);
    retryTimeoutRef.current = setTimeout(() => {
      if (!retryPendingRef.current) return;
      retryPendingRef.current = false;
      retryTimeoutRef.current = null;
      window.location.reload();
    }, RETRY_RELOAD_TIMEOUT_MS);
    setAcquireNonce((n) => n + 1);
  }, []);

  // Resolve an armed retry on its acquire outcome. `ready` → recovered, disarm.
  // `denied`/`error` → the in-document retry is hopeless (iOS standalone won't
  // re-prompt), so reload to a fresh document. `idle`/`loading` are the
  // transient acquire states — keep waiting. Only ever acts when a retry is
  // armed, so the initial denied screen (no tap yet) never triggers a reload.
  useEffect(() => {
    if (!retryPendingRef.current) return;
    if (status === "ready") {
      clearRetryWatchdog();
    } else if (status === "denied" || status === "error") {
      clearRetryWatchdog();
      window.location.reload();
    }
  }, [status, clearRetryWatchdog]);

  // If the overlay closes (or the hook unmounts) with a retry still armed, drop
  // it — a pending reload must never fire after the user has left the camera.
  useEffect(() => {
    if (!isOpen) clearRetryWatchdog();
    return () => clearRetryWatchdog();
  }, [isOpen, clearRetryWatchdog]);

  // Continuous zoom (pinch + chip snap share this path). Clamps to
  // [zoomFloor, zoomCeiling]; a no-op on the device-switch ultra-wide fallback or
  // while not ready. Routes to native applyConstraints (optical/fused, incl. below
  // 1×) or the digital previewScale.
  const setZoom = useCallback(
    (factor: number) => {
      if (status !== "ready" || onUltraWide || zoomCeiling <= zoomFloor) return;
      const clamped = Math.min(zoomCeiling, Math.max(zoomFloor, factor));
      setWideZoom(clamped);
      const native = nativeZoomRef.current;
      const track = trackRef.current;
      if (native && track) {
        void applyNativeZoom(track, clamped); // optical / fused, incl. below 1×
      } else {
        setPreviewScale(clamped); // digital — preview + captureFrame read it
      }
    },
    [status, onUltraWide, zoomCeiling, zoomFloor, applyNativeZoom],
  );

  // Snap to a preset stop. A factor the CURRENT track's native range can reach
  // (incl. a fused camera's sub-1× floor) is a plain zoom on this track — no
  // re-acquire. Otherwise 0.5 ↔ a wide factor crosses physical lenses (the
  // device-switch ultra-wide fallback).
  const selectStop = useCallback(
    (factor: number) => {
      if (status !== "ready" || !lensStops.includes(factor)) return;
      const nativeCanReach =
        !onUltraWide && factor >= zoomFloor - ZOOM_EPSILON;
      if (nativeCanReach) {
        setZoom(factor); // native or digital snap, same path as pinch
        return;
      }
      // The acquire effect's cleanup stops the current tracks FIRST, then the
      // re-run acquires the target lens — status passes through `loading` and
      // torch support is re-probed on the new track.
      const wantUW = factor < 1;
      if (wantUW !== onUltraWide) {
        wantUltraWideRef.current = wantUW;
        pendingWideZoomRef.current = wantUW ? 1 : factor;
        setAcquireNonce((n) => n + 1);
      }
      // wantUW && onUltraWide → already on the device ultra-wide → no-op
    },
    [status, lensStops, onUltraWide, zoomFloor, setZoom],
  );

  return {
    videoRef,
    status,
    torchSupported,
    torchOn,
    toggleTorch,
    captureFrame,
    retry,
    lensStops,
    activeZoom: onUltraWide ? 0.5 : wideZoom,
    zoomFloor,
    zoomCeiling,
    previewScale,
    setZoom,
    selectStop,
  };
}
